import { useCallback, useEffect, useRef, useState } from 'react';

import { APOLLO_CLIENTS, CustomContext } from '@/graphql/client';
import { useOfflinePaymentAvailabilityLazyQuery } from '@/graphql/orderApp/api';
import type { PaymentGatewayConfig } from '@/graphql/saleor/api';
import { usePaymentGatewayInitializeMutation } from '@/graphql/saleor/api';
import { IS_TEST_ENV, PAYMENT_APPS_ID } from '@/lib/const';
import type { MulticardConfig } from '@/lib/payment/types';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import {
  paymentGatewaysActions,
  paymentGatewaysState,
} from '@/states/paymentGatewaysState';

export const useGetPaymentGateways = (
  orderOrCheckoutId?: string,
  amount?: number,
  billingAddressCountryCode?: string,
) => {
  const { country, locale } = useRegionProvider();
  const [mutation] = usePaymentGatewayInitializeMutation();
  const [queryOfflinePaymentAvailability] =
    useOfflinePaymentAvailabilityLazyQuery({
      context: CustomContext({
        disableErrorNotification: true,
        client: APOLLO_CLIENTS.orderApp,
      }),
    });
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasFoundPaymentMethods, setHasFoundPaymentMethods] = useState(false);
  const processingRef = useRef(false);

  const shouldSkipFetch = useCallback(() => {
    return !orderOrCheckoutId || amount === undefined || amount === 0;
  }, [orderOrCheckoutId, amount]);

  const parseGatewayConfigs = useCallback((configs: PaymentGatewayConfig[]) => {
    let multicardConfig: MulticardConfig | null = null;

    const gatewayConfigs = configs.map(({ data, id }) => {
      const paymentMethods = data?.paymentMethods?.map(
        ({
          name,
          type,
          ...dropInConfiguration
        }: Record<string, string | string[]>) => {
          return {
            name,
            type,
            dropInConfiguration,
          };
        },
      );

      if (data?.multicardMaxPayments) {
        multicardConfig = {
          paidMoney: data?.paidMoney,
          maxPayments: data?.multicardMaxPayments,
          maxAmount: data?.nextPaymentMaxAmount,
          minAmount: data?.nextPaymentMinAmount,
        } as MulticardConfig;
      }

      return { id, paymentMethods };
    });

    if (multicardConfig) {
      paymentGatewaysActions.setMulticardConfig(multicardConfig);
    }

    return gatewayConfigs;
  }, []);

  const fetchPaymentGateways = useCallback(
    async (forceRefresh = false) => {
      if ((!forceRefresh && processingRef.current) || shouldSkipFetch()) {
        setLoading(false);
        return;
      }

      processingRef.current = true;
      const keepPartialAmount =
        orderOrCheckoutId !== paymentGatewaysState.id &&
        amount === paymentGatewaysState.amount;
      paymentGatewaysActions.clear(keepPartialAmount);

      try {
        const { data } = await mutation({
          variables: {
            id: orderOrCheckoutId!,
            amount,
            paymentGateways: PAYMENT_APPS_ID.map((id) => ({
              id,
              data: {
                countryCode: billingAddressCountryCode || country.code,
                shopperLocale: locale.locale,
              },
            })),
          },
          context: CustomContext({
            disableErrorNotification: true,
          }),
        });

        const configs =
          data?.paymentGatewayInitialize?.gatewayConfigs?.filter(
            ({ data }) => !!data?.paymentMethods,
          ) ?? [];

        const gatewayConfigs = parseGatewayConfigs(configs);

        const offlinePaymentAvailable = await queryOfflinePaymentAvailability({
          variables: {
            id: orderOrCheckoutId!,
          },
        });

        if (
          offlinePaymentAvailable?.data?.storefrontOfflinePaymentAvailability
        ) {
          gatewayConfigs.push({
            id: PAYMENT_TYPES.OFFLINE,
            paymentMethods: [
              {
                type: PAYMENT_TYPES.OFFLINE,
              },
            ],
          });
        }

        paymentGatewaysActions.set(orderOrCheckoutId!, amount!, gatewayConfigs);

        if (gatewayConfigs.length > 0) {
          setHasFoundPaymentMethods(true);
        }
      } catch (error) {
        console.error('Error fetching payment gateways:', error);
      } finally {
        setHasFetched(true);
        setLoading(false);
        processingRef.current = false;
      }
    },
    [
      mutation,
      orderOrCheckoutId,
      amount,
      billingAddressCountryCode,
      country.code,
      locale.locale,
      parseGatewayConfigs,
      shouldSkipFetch,
    ],
  );

  useEffect(() => {
    fetchPaymentGateways();

    // Reset processingRef on unmount to prevent "no payment available" errors and not in test environment to prevent timeout
    return () => {
      if (!IS_TEST_ENV) {
        processingRef.current = false;
      }
    };
  }, [fetchPaymentGateways]);

  return {
    isLoading: loading,
    hasFetched,
    hasFoundPaymentMethods,
    gatewayConfigs: paymentGatewaysState.configs,
    multicardConfig: paymentGatewaysState.multicardConfig,
    partialAmount: paymentGatewaysState.partialAmount,
    fetchPaymentGateways, // Expose function for manual re-fetching
  };
};
